import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Particle {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  opacity: number;
  scale: number;
}

interface MorningCheckInCompleteProps {
  declaration: string;
  momentumDays?: number;
  onComplete?: () => void;
}

const MorningCheckInComplete: React.FC<MorningCheckInCompleteProps> = ({
  declaration,
  momentumDays = 3,
  onComplete
}) => {
  const [particles, setParticles] = useState<Particle[]>([]);
  const [showDeclaration, setShowDeclaration] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const navigate = useNavigate();

  // Get current time formatted
  const getCurrentTime = () => {
    const now = new Date();
    const timeString = now.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    const dayString = now.toLocaleDateString('en-US', { 
      weekday: 'long' 
    });
    return `${timeString} • ${dayString}`;
  };

  // Trigger haptic feedback on mobile
  const triggerHaptic = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }
  };

  // Create particle burst effect
  const createParticles = () => {
    const newParticles: Particle[] = [];
    const particleCount = 5;
    
    for (let i = 0; i < particleCount; i++) {
      const angle = (Math.PI * 2 * i) / particleCount + Math.random() * 0.5;
      const velocity = 2 + Math.random() * 2;
      
      newParticles.push({
        id: i,
        x: 0,
        y: 0,
        vx: Math.cos(angle) * velocity,
        vy: Math.sin(angle) * velocity,
        opacity: 1,
        scale: 0.8 + Math.random() * 0.4
      });
    }
    
    setParticles(newParticles);
  };

  // Animate particles
  useEffect(() => {
    if (particles.length === 0) return;

    const animateFrame = () => {
      setParticles(prevParticles => 
        prevParticles.map(particle => ({
          ...particle,
          x: particle.x + particle.vx,
          y: particle.y + particle.vy,
          opacity: Math.max(0, particle.opacity - 0.02),
          scale: particle.scale * 0.99
        })).filter(particle => particle.opacity > 0)
      );
    };

    const intervalId = setInterval(animateFrame, 16);
    return () => clearInterval(intervalId);
  }, [particles]);

  // Initial setup
  useEffect(() => {
    console.log('MorningCheckInComplete mounted with declaration:', declaration);
    triggerHaptic();
    createParticles();
    
    // Show declaration with delay for animation
    const declarationTimer = setTimeout(() => {
      setShowDeclaration(true);
    }, 800);

    // Show button after declaration animation
    const buttonTimer = setTimeout(() => {
      setShowButton(true);
    }, 1400);

    // Removed auto-advance - user must click Continue button

    return () => {
      clearTimeout(declarationTimer);
      clearTimeout(buttonTimer);
    };
  }, [navigate, onComplete]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-white">
      <div className="text-center max-w-md w-full space-y-4">

        {/* Time and day */}
        <div className="text-xs text-gray-400 mb-2">
          {getCurrentTime()}
        </div>

        {/* Main completion message with particles */}
        <div className="relative inline-block">
          <h1 className="text-xl font-bold text-green-600 mb-2 animate-pulse">
            Clarity created ✓
          </h1>
          
          {/* Particle container */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            {particles.map(particle => (
              <div
                key={particle.id}
                className="absolute w-2 h-2 rounded-full bg-gradient-to-r from-blue-400 to-green-400"
                style={{
                  transform: `translate(${particle.x}px, ${particle.y}px) scale(${particle.scale})`,
                  opacity: particle.opacity,
                  transition: 'none'
                }}
              />
            ))}
          </div>
        </div>

        {/* Focus declaration */}
        <div className="space-y-3">
          <div 
            className={`transform transition-all duration-700 ease-out ${
              showDeclaration 
                ? 'translate-y-0 opacity-100' 
                : 'translate-y-8 opacity-0'
            }`}
          >
            <div 
              className="bg-white border-2 border-green-100 rounded-xl p-4 shadow-lg transform transition-all duration-500"
              style={{
                animation: showDeclaration ? 'bounce 0.8s ease-out 0.2s 1' : 'none',
                transform: showDeclaration ? 'scale(1)' : 'scale(0.95)',
              }}
            >
              <p className="text-base font-medium text-gray-800 italic">
                {declaration}
              </p>
            </div>
          </div>
        </div>

        {/* Momentum counter */}
        <div className="flex items-center justify-center space-x-2 text-gray-600 py-2">
          <span className="text-lg">🔥</span>
          <span className="font-medium text-sm">
            {momentumDays} {momentumDays === 1 ? 'day' : 'days'} focused
          </span>
        </div>


        {/* Continue button */}
        <div 
          className={`transform transition-all duration-700 ease-out ${
            showButton 
              ? 'translate-y-0 opacity-100' 
              : 'translate-y-4 opacity-0'
          }`}
        >
          <button
            onClick={() => {
              if (onComplete) {
                onComplete();
              } else {
                navigate('/dashboard');
              }
            }}
            className="mt-4 px-6 py-2.5 bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-lg font-medium hover:from-blue-600 hover:to-green-600 transition-all duration-200 shadow-lg text-sm"
          >
            Continue
          </button>
        </div>
      </div>

    </div>
  );
};

export default MorningCheckInComplete;